import React, {
	Fragment,
	useState,
	useMemo,
	useCallback,
	useEffect,
} from "react";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import {
	CalendarNav,
	CalendarNext,
	CalendarPrev,
	CalendarToday,
	SegmentedGroup,
	SegmentedItem,
	Eventcalendar,
	MbscCalendarEvent,
	MbscPageLoadingEvent,
} from "@mobiscroll/react";
import { useNavigate } from "react-router-dom";
import { useEventManager } from "../hooks/useEventManager";
import { Container, Row, Col } from "react-bootstrap";
import EventFilters from "../components/EventFilter";
import { Loader } from "../components/Loader";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import "./Home.css";

/**
 * Which event sources each Event Type choice asks the API for.
 *
 * A normal school week holds ~1,717 events, ~1,545 of them Edval timetable and
 * room bookings (`liss`) or Clipboard co-curricular sessions — neither carries an
 * approval block, so neither can be managed here. Selecting a type now restricts
 * the REQUEST rather than sifting the response, so the rest never crosses the
 * wire.
 *
 * `all` is spelled out rather than sent as the API's own `all` keyword, which is
 * the point: "All" here means all the events someone MANAGES, and Edval
 * timetable and room bookings (`liss`) are deliberately not among them. There is
 * no radio for them and no way to reach them, because there is nothing to do
 * with one if you found it -- ~350 a week across only 49 distinct titles, none
 * carrying a banner or an approval block, and anything attached to one is wiped
 * by the next sync.
 *
 * Module-level so each array keeps ONE identity across renders: they feed a
 * react-query key and a useCallback dependency list, and a fresh array every
 * render would refetch the calendar on every render.
 */
const CATEGORIES_FOR_TYPE: Record<string, string[]> = {
	all: ["edumate", "clipboard", "manual"],
	edumate: ["edumate"],
	clipboard: ["clipboard"],
	manual: ["manual"],
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Home = (_props: any) => {
	const date = new Date();
	const [selectedView, setSelectedView] = useState(() => {
		const storedData = sessionStorage.getItem("EventView");
		return storedData ? JSON.parse(storedData) : "month";
	});
	const [selectedType, setSelectedType] = useState(
		getCookieValue("EventType") || "edumate"
	);
	const [selectedFilterText, setSelectedFilterText] = useState(
		getCookieValue("EventText") || ""
	);
	const [selectedFilterLocation, setSelectedFilterLocation] = useState(
		getCookieValue("EventLocation") || ""
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [EventDate, setEventDate] = useState(() => {
		const storedData = sessionStorage.getItem("EventDate");
		return storedData ? JSON.parse(storedData) : date;
	});
	const [filteredEvents, setFilteredEvents] = useState(() => {
		const storedData = sessionStorage.getItem("EventData");
		return storedData ? JSON.parse(storedData) : [];
	});
	// The Event Type radio now drives the API rather than a pass over what was
	// already fetched. Module-level so each entry keeps one identity: these arrays
	// end up in a react-query key and a useCallback dependency list.
	const categories = CATEGORIES_FOR_TYPE[selectedType] ?? CATEGORIES_FOR_TYPE.edumate;

	const { events, eventsLoaded, getLazyData } =
		useEventManager(undefined, categories);
	const [showLoader, setShowloader] = useState(false);
	const navigate = useNavigate();

	const filterData = {
		text: selectedFilterText,
		location: selectedFilterLocation,
		type: selectedType,
	};

	// Configure time range to show for schedule view
	const scheduleTimeRange = useMemo(() => {
		return { startTime: "05:30", endTime: "21:30" };
	}, []);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [calView, setCalView] = useState<any>({
		calendar: { labels: true },
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleEventClick = useCallback((e: any) => {
		navigate("/event/" + e.event.id);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleFilterText = (value: string) => {
		setSelectedFilterText(value);
		setCookie("EventText", value, 30);
		filterEvents(events, selectedType, value, selectedFilterLocation);
	};

	const handleFilterLocation = (value: string) => {
		setSelectedFilterLocation(value);
		setCookie("EventLocation", value, 30);
		filterEvents(events, selectedType, selectedFilterText, value);
	};

	const handleTypeChange = (type: string) => {
		setSelectedType(type);
		setCookie("EventType", type, 30);
		filterEvents(events, type, selectedFilterText, selectedFilterLocation);
	};

	const filterEvents = React.useCallback(
		(
			events: MbscCalendarEvent,
			selected: string,
			filterText: string,
			filterLocation: string
		) => {
			const evs: MbscCalendarEvent[] = [];
			for (let i = 0; i < events.length; ++i) {
				const item = events[i];
				if (item.Source === "edumate" && item.Attendees.length > 0)
					console.log("Attendees: ", item.Attendees);
				// No type comparison here: the API returns only the selected
				// sources now. Re-checking would also be WRONG — it compares a
				// source id against the `Category` label, and those differ for
				// LISS, whose events are still labelled `MyPymble`.
				if (
					item["title"].toLowerCase().indexOf(filterText.toLowerCase()) !==
						-1 &&
					(filterLocation === "" ||
						(item["location"] &&
							item["location"]
								.toLowerCase()
								.indexOf(filterLocation.toLowerCase()) !== -1))
				) {
					evs.push(item);
				}
			}
			setFilteredEvents(evs);
			//sessionStorage.setItem("EventData", JSON.stringify(evs));
		},
		[]
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChangeView = (e: any) => {
		let mode = e.target.value;
		setView(mode);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleDateSelect = (e: any) => {
		let selectedDate = e.date.getTime() - date.getTimezoneOffset() * 60000;
		sessionStorage.setItem("EventDate", JSON.stringify(selectedDate));
	};

	const setView = (mode: string) => {
		let newView;
		switch (mode) {
			case "agenda":
				newView = {
					calendar: { type: "week" },
					agenda: { type: "week" },
				};
				break;
			case "day":
				newView = {
					calendar: { type: "week" },
					schedule: { type: "day", ...scheduleTimeRange },
				};
				break;
			case "week":
				newView = {
					calendar: { type: "week" },
					schedule: { type: "week", ...scheduleTimeRange },
				};
				break;
			case "month":
				newView = {
					calendar: { labels: true },
				};
				break;
		}

		setSelectedView(mode);
		setCalView(newView);
		sessionStorage.setItem("EventView", JSON.stringify(mode));
	};

	const customWithNavButtons = () => {
		return (
			<Fragment>
				<CalendarNav className="cal-header-nav" />
				<div className="cal-header-picker">
					<SegmentedGroup value={selectedView} onChange={handleChangeView}>
						<SegmentedItem value="month">Month</SegmentedItem>
						<SegmentedItem value="week">Week</SegmentedItem>
						<SegmentedItem value="day">Day</SegmentedItem>
						<SegmentedItem value="agenda">Agenda</SegmentedItem>
					</SegmentedGroup>
				</div>
				<CalendarPrev className="cal-header-prev" />
				<CalendarToday className="cal-header-today" />
				<CalendarNext className="cal-header-next" />
			</Fragment>
		);
	};

	useEffect(() => {
		if (eventsLoaded) {
			filterEvents(
				events,
				selectedType,
				selectedFilterText,
				selectedFilterLocation
			);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [events]);

	useEffect(() => {
		setView(selectedView);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function setCookie(name: string, value: string, expirationDays: number) {
		date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000);
		const expires = "; expires=" + date.toUTCString();
		document.cookie = name + "=" + value + expires + "; path=/";
	}

	function getCookieValue(name: string) {
		const cookieString = document.cookie;
		const cookies = cookieString.split(";");
		for (let i = 0; i < cookies.length; i++) {
			const cookie = cookies[i].trim();
			if (cookie.startsWith(name + "=")) {
				return cookie.substring(name.length + 1, cookie.length);
			}
		}
		return null;
	}

	// `getLazyData` MUST be in the dependency list: it now closes over the selected
	// categories, so an empty list would keep paging with whatever was chosen on
	// first render and silently ignore the filter from then on.
	const onPageLoading = useCallback((event: MbscPageLoadingEvent) => {
		setShowloader(true);
		getLazyData(event.firstDay, event.lastDay).then(() => {
			setShowloader(false);
		});
	}, [getLazyData]);

	return (
		<Container fluid className="mt-3">
			<Row>
				<Col md={2} className="mt-1">
					<EventFilters
						initialData={filterData}
						onTypeValueChange={handleTypeChange}
						onTextValueChange={handleFilterText}
						onLocationValueChange={handleFilterLocation}
					/>
				</Col>
				<Col className="calendarContainer">
					<Eventcalendar
						theme="auto"
						className="event-calendar"
						themeVariant="light"
						data={filteredEvents}
						view={calView}
						renderHeader={customWithNavButtons}
						defaultSelectedDate={EventDate}
						onEventClick={handleEventClick}
						onSelectedDateChange={handleDateSelect}
						onPageLoading={onPageLoading}
					/>
				</Col>
			</Row>
			{showLoader && <Loader />}
		</Container>
	);
};
